/***
 * Created on 2023/9/15
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useRef, useState} from 'react';
import ModalDialog from "../../common/modal_dialog/modalDialog";
import {useTranslation} from "react-i18next";
import TextInput from "../../common/form/text_input";
import {useOnChange} from "../../../hook/form";
import {domains} from "../../../service/common";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {apiGet, apiPost} from "../../../service/api";
import {getLocalUser} from "../../../service/user";
import {useCountdown} from "../../common/countdown";
import {use} from "i18next";


function AddEmailWizard({showModal, closeModel, ...props}) {
    const { t } = useTranslation();
    const [showPopup, setShowPopup] = useState(false);
    const [next, setNext] = useState(false);
    const {seconds, setSeconds} = useCountdown(0);
    const [disabled, setDisabled] = useState(false);
    const modalRef = useRef();
    const localUser = getLocalUser();
    const {onChange, setValues, values} = useOnChange({
        email: (localUser?.emails && !localUser?.emails?.[0]?.is_verified) ? localUser?.emails?.[0]?.email : ''
    });
    const clientQuery = useQueryClient();

    const countdown = useQuery({
        queryKey: ['get_countdown'],
        queryFn: async () => {
            const data = await apiGet('/ajax/countdown');
            const now = new Date().getTime();
            const value = Math.round((now - data?.countdown) / 1000) || 0;
            setSeconds(value > 60 ? 0 : 60 - value);
            setDisabled(false);
            return data;
        }
    });

    const addEmailMutation = useMutation({
        mutationFn: async (data) => await apiPost(`/ajax/add_email`, data),
        onSuccess: data => {
            setNext(true);
        },
        onError: (error, variables, context) => {}
    });

    const onEmailChange = (e) => {
        if (e.target.value.includes('@')) {
            setShowPopup(true);
        }
        else {
            setShowPopup(false);
        }
        onChange(e);
        modalRef?.current?.disableSubmitButton(!e.target.checkValidity());
    }

    const addEmail = async () => {
        setDisabled(true);
        await addEmailMutation.mutateAsync({email: values.email});
        await countdown.refetch();
    }

    const onPopupClick = (e) => {
        const email =e.target.innerText;
        setValues({email: email});
        setShowPopup(false);
        const valid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/.test(email);
        modalRef?.current?.disableSubmitButton(!valid);
    }

    const Step2 = () => {
        const onClose = () => {
            clientQuery.invalidateQueries({
                queryKey: ['profile']
            })
            closeModel();
        }
        return (
            <ModalDialog {...props} title={t('@Add Email@')} showModal={showModal} closeModal={onClose} hideSubmit={true} cancelText={t('@OK@')}>
                <div className="dialog-content">
                    <div className="dialog-content__pinned-msg dialog-content__pinned-msg--info mb-16">
                        {t('@Recommend you to finish the activation first then close this page.@')}
                    </div>

                    <div className="dialog__form-group">
                        <label>{t('@The activation email has been sent to@')}</label>
                        <div className="comp-input">
                            <div className="input-field--hybrid">
                                <input type="text" defaultValue={values.email} disabled={true}/>
                                <button className="q-btn q-btn-default" onClick={addEmail} disabled={seconds > 0 || disabled}>{seconds > 0 ? (seconds) : t('@Send@')}</button>
                            </div>
                        </div>
                    </div>
                    <p className="text-remind">{t('@Please check your email to activate myQNAPcloud account, and then you can sign in again.@')}</p>
                    <p className="text-note">
                        {t('@If you have not received the activation mail, please check the spam folder in your mail client first. We suggest to add myQNAPcloud announce email account no-reply@qnap.com to your contact list to avoid the email accidentally diverted to your spam folder.@')}
                    </p>
                </div>
            </ModalDialog>
        );
    }

    return (
        <>
            {!next &&
                <ModalDialog {...props} title={t('@Add Email@')} showModal={showModal} closeModal={closeModel}
                             submitText={t('@Next@')} submitAction={addEmail} ref={modalRef}>
                    <div className="dialog-content">
                        <TextInput label={t('@Email@')} compType="dialog" className={'w-full max-w-full'}
                                   inputMode={"email"}
                                   required={true}
                                   // type={'email'}
                                   pattern="^[a-zA-Z0-9+\-_%\.]+@[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,63}$"
                                   placeholder={t('@Email@')} onChange={onEmailChange} value={values.email || ''}
                                   name='email'/>
                        <div className={`comp-popover ${showPopup ? '' : 'hide'}`}>
                            <ul className="popover-menu !w-full">
                                {
                                    domains
                                        .filter(name => name.match(values?.email?.split('@')?.[1] ?? ''))
                                        .slice(0, 5)
                                        .map(d => <li className="popover-menu__item cursor-pointer"
                                                      key={d} onClick={onPopupClick}>{values?.email?.split('@')?.[0] ?? ''}{d}</li>)
                                }
                            </ul>
                        </div>
                    </div>
                </ModalDialog>
            }
            {next && <Step2 />}
        </>
    );
}

export default AddEmailWizard;